import React, { useState, useRef, useEffect } from 'react';
import { Send, Paperclip, X, Loader, AtSign, Video, Mic, Trash2 } from 'lucide-react';
import { useChat } from '../../contexts/ChatContext';
import { ChatRoom, ChatMessage } from '../../services/chatService';
import { useOrganization } from '../../contexts/OrganizationContext';
import { projectMemberService } from '../../services/projectMemberService';
import { supabase } from '../../lib/supabase';
import ScreenRecorderModal from './ScreenRecorderModal';
import { extractImageFromClipboard, buildPastedImageName } from './clipboardImage';
import { useAudioRecorder } from '../../hooks/useAudioRecorder';
import {
  nomeDoArquivoDeAudio,
  formatarDuracaoDeAudio,
  DURACAO_MAXIMA_SEGUNDOS
} from '../../hooks/audioRecorderFormat';

// Usando a mesma interface Member do ProjectMembers.tsx
interface Member {
  id: string;
  name: string;
  email?: string;
  role: string;
  avatar?: string;
  phone?: string;
  department?: string;
  joinDate?: string;
}

// Interface para mencionar usuários
interface UserForMention {
  id: string;
  name: string;
  avatar?: string;
}

// We'll use any for raw database results and transform them safely
type DatabaseResult = any;

// Exportado para teste isolado. mentionUsers já chega único por id (Map na montagem
// da lista) — não deduplicar por nome aqui, ou membros com nomes iguais somem da lista.
export function filterMentionUsers(
  users: UserForMention[],
  search: string
): UserForMention[] {
  return users.filter(user =>
    user.name.toLowerCase().includes(search.toLowerCase())
  );
}

// Quem o Enter/Tab confirmam na lista de menções. `indiceSelecionado` vem -1
// enquanto o usuário só digitou (sem navegar com as setas) — nesse caso vale o
// primeiro da lista, que é o que aparece destacado como próxima escolha.
function usuarioParaConfirmar<T>(
  lista: T[],
  indiceSelecionado: number
): T | undefined {
  if (lista.length === 0) return undefined;
  return lista[indiceSelecionado >= 0 ? indiceSelecionado : 0];
}

interface ChatInputProps {
  onSendMessage: (content: string, mentions?: string[]) => void;
  onAttachFile?: (file: File, caption?: string) => void | Promise<void>;
  disabled?: boolean;
  replyingTo?: ChatMessage | undefined;
  onCancelReply?: () => void;
  isAttaching?: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  onAttachFile,
  disabled = false,
  replyingTo,
  onCancelReply,
  isAttaching = false
}) => {
  const [message, setMessage] = useState('');
  const [mentionSearch, setMentionSearch] = useState('');
  const [showMentions, setShowMentions] = useState(false);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(-1);
  const [mentions, setMentions] = useState<string[]>([]);
  const [mentionUsers, setMentionUsers] = useState<UserForMention[]>([]);
  const { members, currentRoom } = useChat();
  const { organization } = useOrganization();
  const [projectMembers, setProjectMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRecorderModalOpen, setIsRecorderModalOpen] = useState(false);
  const [pastedImage, setPastedImage] = useState<File | null>(null);
  const [pastedImageUrl, setPastedImageUrl] = useState<string | null>(null);

  const {
    isRecording,
    recordingTime,
    error: erroDeAudio,
    startRecording,
    stopRecording,
    cancelRecording
  } = useAudioRecorder();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mentionListRef = useRef<HTMLDivElement>(null);

  // Buscar membros do projeto usando o novo serviço unificado
  useEffect(() => {
    const fetchProjectMembers = async () => {
      if (!currentRoom?.project_id) {
        // Conversa privada (ou sala sem projeto): nada a mencionar. Sem zerar,
        // o autocomplete oferecia os membros do projeto aberto antes.
        setProjectMembers([]);
        return;
      }

      try {
        setLoading(true);
        console.log('ChatInput: Buscando membros para projeto:', currentRoom.project_id);
        
        // Usar o novo serviço para buscar membros
        const projectMembersData = await projectMemberService.getMembersForMention(currentRoom.project_id);
        
        // Converter para o formato esperado pelo componente
        const formattedMembers: Member[] = projectMembersData.map(member => ({
          id: member.id,
          name: member.name || 'Usuário sem nome',
          avatar: member.avatar,
          role: 'Membro', // Role será mostrado nas menções
          department: `Projeto: ${currentRoom.name}`
        }));
        
        console.log('ChatInput: Membros carregados via novo serviço:', formattedMembers);
        setProjectMembers(formattedMembers);
        
      } catch (error) {
        console.error('ChatInput: Erro ao buscar membros do projeto:', error);
        
        // Fallback: buscar apenas membros da organização se houver erro
        if (organization?.id) {
          try {
            const { data: orgMembers, error: orgError } = await supabase
              .from('organization_members')
              .select(`
                id,
                role,
                user_id,
                user_profile!inner (
                  id,
                  auth_id,
                  name,
                  user_photo
                )
              `)
              .eq('organization_id', organization.id);
            
            if (orgError) throw orgError;
            
            const fallbackMembers: Member[] = (orgMembers || []).map((member: any) => ({
              id: member.user_profile?.auth_id || member.id,
              name: member.user_profile?.name || 'Usuário sem nome',
              avatar: member.user_profile?.user_photo,
              role: member.role || 'Membro',
              department: 'Organização'
            }));
            
            console.log('ChatInput: Usando fallback - membros da organização:', fallbackMembers);
            setProjectMembers(fallbackMembers);
          } catch (fallbackError) {
            console.error('ChatInput: Erro no fallback:', fallbackError);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProjectMembers();
  }, [currentRoom?.project_id, currentRoom?.name, organization?.id]);

  // Carregar usuários disponíveis para menção - Chat members + Project members
  useEffect(() => {
    // Começar com membros do chat
    const chatUsers = members.map(m => ({
      id: m.user_auth_id,
      name: m.user?.name || `Membro ${m.user_auth_id.substring(0, 5)}`,
      avatar: m.user?.avatar_url
    }));
    
    // Criar um Map para evitar duplicações com base apenas no ID
    const userMap = new Map<string, UserForMention>();
    
    // Adicionar membros do chat ao Map
    chatUsers.forEach(user => {
      userMap.set(user.id, user);
    });
    
    // Adicionar membros do projeto ao Map (se ainda não existirem)
    if (projectMembers.length > 0) {
      console.log(`Processando ${projectMembers.length} membros do projeto para menção`);
      
      projectMembers.forEach(member => {
        // Verificar se o usuário já existe no Map
        if (!userMap.has(member.id)) {
          userMap.set(member.id, {
            id: member.id,
            name: member.name,
            avatar: member.avatar
          });
        }
      });
    }
    
    // Converter o Map de volta para array
    const allUsers = Array.from(userMap.values());
    
    setMentionUsers(allUsers);
    console.log(`Total de usuários disponíveis para menção após remoção de duplicatas: ${allUsers.length}`);
  }, [members, projectMembers]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [message]);

  // Detectar quando @ é digitado
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setMessage(newValue);

    const cursorPosition = e.target.selectionStart;
    const textBeforeCursor = newValue.substring(0, cursorPosition);
    
    // Verificar se o usuário está tentando mencionar alguém
    const lastAtSymbol = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtSymbol !== -1) {
      const lastSpace = textBeforeCursor.lastIndexOf(' ', lastAtSymbol);
      const isAtStart = lastSpace === -1 ? lastAtSymbol === 0 : true;
      
      if (isAtStart) {
        const searchText = textBeforeCursor.substring(lastAtSymbol + 1);
        setMentionSearch(searchText);
        setShowMentions(true);
        setSelectedMentionIndex(-1);
        return;
      }
    }
    
    // Esconder a lista de menções se não estiver mais digitando uma menção
    setShowMentions(false);
  };

  // Inserir a menção no texto
  const insertMention = (user: UserForMention) => {
    if (!textareaRef.current) return;
    
    const cursorPosition = textareaRef.current.selectionStart;
    const textBeforeCursor = message.substring(0, cursorPosition);
    const textAfterCursor = message.substring(cursorPosition);
    
    // Encontrar o último @ antes do cursor
    const lastAtSymbol = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtSymbol !== -1) {
      // Substituir o texto digitado após @ com o nome do usuário
      const newText = 
        textBeforeCursor.substring(0, lastAtSymbol) + 
        `@${user.name} ` + 
        textAfterCursor;
      
      setMessage(newText);
      setShowMentions(false);
      
      // Adicionar o usuário à lista de menções
      if (!mentions.includes(user.id)) {
        setMentions([...mentions, user.id]);
      }
      
      // Focar o textarea novamente
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 0);
    }
  };

  // A imagem colada só sai do campo quando o usuário manda a mensagem, para
  // que um Ctrl+V acidental não publique nada no chat.
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (disabled || isAttaching || !onAttachFile) return;

    const image = extractImageFromClipboard(e.clipboardData?.items);
    if (!image) return;

    e.preventDefault();
    const named = new File([image], buildPastedImageName(image.type), { type: image.type });
    setPastedImage(named);
    setPastedImageUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(named);
    });
  };

  const clearPastedImage = () => {
    setPastedImage(null);
    setPastedImageUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  // Só revoga o object URL ao desmontar; trocar/remover a imagem já revoga o
  // anterior no próprio setter.
  useEffect(() => {
    return () => {
      setPastedImageUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (disabled) return;

    if (pastedImage && onAttachFile) {
      // O envio segue em background (o campo limpa na hora, como antes), mas a
      // rejeição precisa ser consumida: quem chama já avisa a falha em toast, e
      // uma promise solta viraria "unhandled rejection" no console e no Sentry.
      void Promise.resolve(onAttachFile(pastedImage, message.trim() || undefined)).catch(() => {});
      clearPastedImage();
      setMessage('');
      setMentions([]);
      return;
    }

    if (message.trim()) {
      onSendMessage(message.trim(), mentions.length > 0 ? mentions : undefined);
      setMessage('');
      setMentions([]);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Se a lista de menções estiver visível
    if (showMentions) {
      const filteredList = filteredUsers;
      
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedMentionIndex(prev => 
          prev < filteredList.length - 1 ? prev + 1 : prev
        );
        return;
      }
      
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedMentionIndex(prev => (prev > 0 ? prev - 1 : 0));
        return;
      }
      
      // Sem seta pra baixo antes, `selectedMentionIndex` ainda é -1 (o
      // handleInputChange zera a cada tecla). Exigir >= 0 aqui fazia o Enter
      // escapar pro envio: a mensagem saía com o texto cru e sem menção
      // nenhuma, o que também matava a notificação de quem foi mencionado.
      // O Tab abaixo sempre teve esse fallback; o Enter tem que ter o mesmo.
      if (e.key === 'Enter' && filteredList.length > 0) {
        e.preventDefault();
        const user = usuarioParaConfirmar(filteredList, selectedMentionIndex);
        if (user) {
          insertMention(user);
        }
        return;
      }
      
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowMentions(false);
        return;
      }
      
      if (e.key === 'Tab') {
        e.preventDefault();
        const user = usuarioParaConfirmar(filteredList, selectedMentionIndex);
        if (user) {
          insertMention(user);
        }
        return;
      }
    }
    
    // Enter sem shift para enviar mensagem
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleKeyDownMentionItem = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    // Navegar pela lista com as setas
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextItem = mentionListRef.current?.querySelectorAll('button')[index + 1];
      if (nextItem) {
        (nextItem as HTMLButtonElement).focus();
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (index === 0) {
        // Voltar para o textarea
        textareaRef.current?.focus();
      } else {
        const prevItem = mentionListRef.current?.querySelectorAll('button')[index - 1];
        if (prevItem) {
          (prevItem as HTMLButtonElement).focus();
        }
      }
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const user = filteredUsers[index];
      if (user) {
        insertMention(user);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setShowMentions(false);
      textareaRef.current?.focus();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0 && onAttachFile) {
      // Mesma razão do envio de imagem colada: a falha é avisada por quem
      // chama, aqui só a rejeição é consumida.
      void Promise.resolve(onAttachFile(files[0])).catch(() => {});
      // Reset the input
      e.target.value = '';
    }
  };

  const handleSendRecordedVideo = async (videoFile: File) => {
    if (!onAttachFile) return;
    // Propositalmente aguardado: o modal de gravação só fecha (e só avisa que
    // deu certo) depois que o envio termina. Sem o await, um upload que
    // falhasse fechava o modal como se o vídeo tivesse sido enviado, e a
    // gravação se perdia.
    await onAttachFile(videoFile);
  };

  const handleGravarAudio = () => {
    void startRecording();
  };

  const handleEnviarAudio = async () => {
    if (!onAttachFile) return;

    const blob = await stopRecording();
    // Um clique em "enviar" antes de o primeiro chunk chegar não deve virar uma
    // mensagem com um arquivo de zero byte.
    if (!blob || blob.size === 0) return;

    const arquivo = new File([blob], nomeDoArquivoDeAudio(blob.type, new Date()), {
      type: blob.type
    });

    // A falha já é avisada em toast por quem chama (ChatRoom); aqui a rejeição
    // só é consumida para não virar "unhandled rejection".
    try {
      await onAttachFile(arquivo);
    } catch {
      // silêncio proposital — ver comentário acima
    }
  };

  // Filtrar usuários com base no texto digitado após @
  const filteredUsers = filterMentionUsers(mentionUsers, mentionSearch);

  return (
    <div className="border-t bg-white w-full max-w-full min-h-[40px] p-4 sm:p-4 p-2">
      {replyingTo && (
        <div className="flex items-start justify-between mb-2 p-3 rounded-lg border-l-4" style={{
          backgroundColor: 'rgba(254, 110, 96, 0.08)',
          borderLeftColor: 'var(--salmon)'
        }}>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-700 mb-1">
              Respondendo a {replyingTo.user?.name || 'Usuário'}
            </div>
            <div className="text-sm text-gray-600 truncate">
              {replyingTo.content}
            </div>
          </div>
          {onCancelReply && (
            <button 
              onClick={onCancelReply}
              className="ml-2 text-gray-400 hover:text-gray-600 flex-shrink-0"
              title="Cancelar resposta"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      )}
      
      {isAttaching && (
        <div className="flex items-center justify-between mb-2 p-2 rounded" style={{
          backgroundColor: 'rgba(254, 110, 96, 0.08)'
        }}>
          <div className="text-sm flex items-center" style={{ color: 'var(--salmon-dark)' }}>
            <Loader className="w-4 h-4 mr-2 animate-spin" />
            Enviando arquivo...
          </div>
        </div>
      )}

      {erroDeAudio && (
        <div role="alert" className="mb-2 p-2 rounded text-sm bg-red-50 text-red-700 border border-red-200">
          {erroDeAudio}
        </div>
      )}

      {pastedImageUrl && (
        <div className="mb-2 inline-flex items-start gap-2 p-2 border border-gray-200 rounded-lg bg-gray-50">
          <img
            src={pastedImageUrl}
            alt="Imagem colada"
            className="max-h-24 max-w-[160px] rounded object-cover"
          />
          <button
            type="button"
            onClick={clearPastedImage}
            className="text-gray-400 hover:text-gray-600"
            title="Remover imagem"
            aria-label="Remover imagem colada"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex items-end gap-2 relative">
        {isRecording ? (
          <div
            role="group"
            aria-label="Gravando áudio"
            className="flex-1 flex items-center gap-3 rounded-lg border border-gray-300 p-3"
          >
            <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
            <span className="text-sm font-medium text-gray-700 tabular-nums">
              {formatarDuracaoDeAudio(recordingTime)}
            </span>
            <span className="text-xs text-gray-400 truncate">
              Gravando... (máx. {formatarDuracaoDeAudio(DURACAO_MAXIMA_SEGUNDOS)})
            </span>
          </div>
        ) : (
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            onPaste={handlePaste}
            placeholder={
              pastedImage
                ? 'Escreva uma legenda (opcional) e envie'
                : 'Digite sua mensagem... Use @ para mencionar alguém'
            }
            className="w-full resize-none rounded-lg border border-gray-300 p-3 focus:border-salmon focus:ring-1 focus:ring-salmon"
            rows={1}
            disabled={disabled}
          />
          
          {/* Lista de menções - Corrigida para exibir no local correto */}
          {showMentions && filteredUsers.length > 0 && (
            <div 
              ref={mentionListRef}
              className="absolute left-0 bg-white border border-gray-200 rounded-md shadow-lg z-10 max-h-48 overflow-y-auto mb-1 w-64"
              style={{
                bottom: "100%",
                maxWidth: "300px"
              }}
            >
              <div className="p-2 bg-gray-50 border-b text-xs text-gray-500 flex items-center">
                <AtSign className="w-3 h-3 mr-1" />
                Mencionar usuário ({filteredUsers.length})
              </div>
              <div className="py-1">
                {filteredUsers.map((user, idx) => (
                  <button
                    key={user.id}
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 focus:outline-none flex items-center"
                    style={selectedMentionIndex === idx ? {
                      backgroundColor: 'rgba(254, 110, 96, 0.12)'
                    } : {}}
                    onClick={() => insertMention(user)}
                    onKeyDown={(e) => handleKeyDownMentionItem(e, idx)}
                  >
                    <div className="w-6 h-6 rounded-full bg-gray-300 mr-2 flex-shrink-0 overflow-hidden">
                      {user.avatar ? (
                        <img 
                          src={user.avatar} 
                          alt={user.name}
                          className="w-full h-full object-cover" 
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-400 text-white text-xs">
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <span className="truncate">{user.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        )}

        {onAttachFile && !isRecording && (
          <>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              className="hidden"
              disabled={disabled || isAttaching}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50"
              disabled={disabled || isAttaching}
              title="Anexar arquivo"
            >
              <Paperclip className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => setIsRecorderModalOpen(true)}
              className="p-2 text-gray-500 hover:text-coral disabled:opacity-50 transition-colors"
              disabled={disabled || isAttaching}
              title="Gravar tela"
            >
              <Video className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={handleGravarAudio}
              className="p-2 text-gray-500 hover:text-coral disabled:opacity-50 transition-colors"
              disabled={disabled || isAttaching}
              title="Gravar áudio"
              aria-label="Gravar áudio"
            >
              <Mic className="w-5 h-5" />
            </button>
          </>
        )}

        {isRecording && (
          <>
            <button
              type="button"
              onClick={cancelRecording}
              className="p-2 text-gray-500 hover:text-red-600 transition-colors"
              title="Cancelar gravação"
              aria-label="Cancelar gravação"
            >
              <Trash2 className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => { void handleEnviarAudio(); }}
              className="p-2 rounded-full bg-coral-deep text-white hover:bg-coral-press flex items-center justify-center"
              title="Enviar áudio"
              aria-label="Enviar áudio"
            >
              <Send className="w-5 h-5" />
            </button>
          </>
        )}

        {!isRecording && (
          <button aria-label="Enviar"
            type="submit"
            className="p-2 rounded-full bg-coral-deep text-white hover:bg-coral-press disabled:opacity-50 flex items-center justify-center"
            disabled={(!message.trim() && !pastedImage) || disabled}
          >
            <Send className="w-5 h-5" />
          </button>
        )}
      </form>

      <ScreenRecorderModal
        isOpen={isRecorderModalOpen}
        onClose={() => setIsRecorderModalOpen(false)}
        onSendVideo={handleSendRecordedVideo}
      />
    </div>
  );
};

export default ChatInput; 